import { useCallback, useEffect, useState } from 'react';

export type UrlTypeNameMapping = Map<string, ValueType>;

export interface ValueType {
  type: string;
  name: string;
  // Reading it may be lazy/side-effecting (can trigger a fetch — see below).
  data: Promise<Blob>;
}

export function useFileUrlToTypeMapping(
  fileUrls?: string[],
  // Filenames keyed by url, for sources whose url can't carry a
  // Content-Disposition header (e.g. local `blob:` object urls).
  fileNames?: Record<string, string>
): UrlTypeNameMapping {
  const [imageUrlToTypeMap, setImageUrlToTypeMap] = useState(
    new Map<string, ValueType>()
  );

  const fetchUrlTypes = useCallback(async () => {
    const fileUrlToType = await Promise.all(
      (fileUrls ?? []).map(async (fileUrl): Promise<[string, ValueType]> => {
        try {
          const result = await fetch(fileUrl);
          const extractedFileType = result.headers.get('content-type');
          const extractedFileName =
            fileNames?.[fileUrl] ??
            result.headers.get('content-disposition')?.match(/"(.*?)"/)?.[1] ??
            'unknown';
          // Cancel the body: sniffing the type must not download the whole
          // scan. `data` re-fetches lazily on first read.
          result.body?.cancel().catch(() => undefined);
          let bodyPromise: Promise<Blob> | undefined;
          return [
            fileUrl,
            {
              type: extractedFileType ?? 'type-unknown',
              name: extractedFileName,
              get data() {
                if (!bodyPromise) {
                  bodyPromise = fetch(fileUrl).then((response) =>
                    response.blob()
                  );
                }
                return bodyPromise;
              },
            },
          ];
        } catch {
          return [
            fileUrl,
            {
              type: 'image/*', // TODO: Why image/* if error
              name: 'unknown',
              data: Promise.resolve(new Blob()),
            },
          ]; // TODO: huck :(
        }
      })
    );
    setImageUrlToTypeMap(new Map(fileUrlToType));
  }, [fileUrls, fileNames]);

  useEffect(() => {
    if (fileUrls) {
      fetchUrlTypes();
    }
  }, [fileUrls, fetchUrlTypes]);

  return imageUrlToTypeMap;
}
